import { dateTime } from "@/lib/fmt";
import { useState } from "react";
import { toast } from "sonner";
import { MoreHorizontal, RotateCcw } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth, useGet } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export const useInvalidateAll = () => { const qc = useQueryClient(); return () => qc.invalidateQueries(); };
export const FINANCIAL = ["sales", "employee_sales", "employee_upload"];

export function RemoveDialog({ kind, ids, onClose, onRemoved }) {
  const [reason, setReason] = useState("");
  const inval = useInvalidateAll();
  const go = async () => {
    try {
      const { data } = ids.length > 1 ? await api.post(`/records/${kind}/bulk-remove`, { ids, reason }) : await api.post(`/records/${kind}/${ids[0]}/remove`, { reason });
      toast.success(ids.length > 1 ? `${data.removed} archived${data.failed.length ? `, ${data.failed.length} failed` : ""}` : `Archived${data.ai_reports_marked_outdated ? " — AI summaries marked outdated" : ""}. Restore anytime from Removed records.`);
      inval(); onClose(); onRemoved?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="remove-dialog">
        <DialogTitle className="font-display text-2xl">{`Archive ${ids.length > 1 ? `${ids.length} records` : "this record"}?`}</DialogTitle>
        <DialogDescription>It stops counting in dashboards, totals, report cards, exports and AI right away. Your name, the time and your reason are recorded, and it moves to Removed records where it can be restored. Records that are part of a finalized End-of-Night Close can't be archived — correct them with a reason instead.</DialogDescription>
        <input className="w-full h-11 rounded-lg border px-3" placeholder="Reason (required)" aria-label="Reason for archiving" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="remove-reason" />
        <Button className="h-11 bg-[#9E2A2B] hover:bg-[#7f2223]" disabled={reason.trim().length < 3} onClick={go} data-testid="remove-confirm">Archive</Button>
      </DialogContent>
    </Dialog>
  );
}

const fmtVal = (v) => (v === null || v === undefined ? "blank" : typeof v === "number" ? String(v) : String(v));
const fieldLabel = (f) => (f || "").replace(/^cat:/, "category ").replace(/_/g, " ");

function HistoryDialog({ kind, id, onClose }) {
  const { data } = useGet(`/records/${kind}/${id}/history`);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-md" data-testid="history-dialog">
        <DialogTitle className="font-display text-2xl">History</DialogTitle>
        <div className="text-sm space-y-1 max-h-80 overflow-y-auto">
          {(data?.revisions || []).map((h, i) => (
            <div key={`r${i}`} className="border rounded-lg p-2 bg-[#FBF9F5]" data-testid={`history-revision-${i}`}>
              <div className="text-xs text-stone-600">{`${dateTime(h.at)} · ${h.by} · ${(h.action || "").replace(/_/g, " ")}${h.reason ? ` — “${h.reason}”` : ""}`}</div>
              {h.changes.map((c) => <div key={c.field} className="num text-xs" data-testid={`history-change-${i}-${c.field}`}><b>{fieldLabel(c.field)}</b>: {fmtVal(c.before)} → {fmtVal(c.after)}</div>)}
            </div>))}
          {(data?.items || []).map((h) => <div key={h.id}>{`${dateTime(h.created_at)} · ${h.user_name} · ${h.action.replace(/_/g, " ")}${h.summary ? ` — ${h.summary}` : ""}`}</div>)}
          {data && !data.items.length && !(data.revisions || []).length && <div className="text-stone-500">No recorded changes.</div>}</div>
      </DialogContent>
    </Dialog>
  );
}

export function RecordActions({ kind, rec, onEdit, onReplace, onRemoved, testid }) {
  const { user } = useAuth();
  const [dlg, setDlg] = useState(null);
  if (!["owner", "gm", "agm"].includes(user.role)) return null;
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button className="p-1.5 rounded hover:bg-stone-100" aria-label="Record actions" onClick={(e) => e.stopPropagation()} data-testid={testid}><MoreHorizontal className="h-4 w-4" /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-white" onClick={(e) => e.stopPropagation()}>
          {onEdit && <DropdownMenuItem onClick={onEdit} data-testid={`${testid}-edit`}>Edit</DropdownMenuItem>}
          {onReplace && <DropdownMenuItem onClick={onReplace} data-testid={`${testid}-replace`}>Replace upload</DropdownMenuItem>}
          <DropdownMenuItem onClick={() => setDlg("remove")} className="text-[#9E2A2B]" data-testid={`${testid}-remove`}>Archive</DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDlg("history")} data-testid={`${testid}-history`}>View history</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {dlg === "remove" && <RemoveDialog kind={kind} ids={[rec.id]} onRemoved={onRemoved} onClose={() => setDlg(null)} />}
      {dlg === "history" && <HistoryDialog kind={kind} id={rec.id} onClose={() => setDlg(null)} />}
    </>
  );
}

function RemovedRow({ a, act, owner }) {
  const restore = async () => {
    if (a.restore_path) {
      const why = window.prompt("Reason for restoring this inventory count:");
      if (why?.trim()) act(() => api.post(a.restore_path, { reason: why }), "Count restored — stock recalculated");
      return;
    }
    try { await api.post(`/records/removed/${a.id}/restore`, {}); act(null, "Restored"); } catch (e) {
      if (e?.response?.data?.detail?.code !== "day_exists") return toast.error(errMsg(e));
      const why = window.prompt(`${errMsg(e)}\n\nReason for replacing it:`);
      if (why?.trim()) act(() => api.post(`/records/removed/${a.id}/restore`, { replace_reason: why }), "Restored — the replaced record is in Removed records");
    }
  };
  return (
    <div className="py-2 flex flex-wrap items-center gap-2" data-testid={`removed-row-${a.id}`}>
      <span className="flex-1 min-w-[12rem]">{`${a.label} · ${a.removed_by} · ${dateTime(a.removed_at)}${a.reason ? ` — ${a.reason}` : ""}`}</span>
      <Button variant="outline" className="h-8" onClick={restore} data-testid={`restore-${a.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>
      {owner && !FINANCIAL.includes(a.kind) && !a.restore_path && <Button variant="ghost" className="h-8 text-[#9E2A2B]" onClick={() => window.confirm("Permanently delete? This cannot be undone.") && window.confirm("Are you absolutely sure?") && act(() => api.delete(`/records/removed/${a.id}?confirm=PURGE`), "Permanently deleted")} data-testid={`purge-${a.id}`}>Delete forever</Button>}
      {FINANCIAL.includes(a.kind) && <span className="text-[11px] text-stone-500" data-testid={`removed-kept-${a.id}`}>Financial record — kept permanently</span>}
    </div>
  );
}

export function RemovedRecords({ kind, open }) {
  const { user } = useAuth();
  const { data, refetch } = useGet(kind ? `/records/removed?kind=${kind}` : "/records/removed");
  const inval = useInvalidateAll();
  const act = async (fn, msg) => { try { if (fn) await fn(); toast.success(msg); refetch(); inval(); } catch (e) { toast.error(errMsg(e)); } };
  if (!data?.items?.length) return open ? <div className="card-surface p-4 text-sm text-stone-500" data-testid={`removed-${kind || "all"}-empty`}>Nothing has been removed.</div> : null;
  return (
    <details open={open} className="card-surface p-3 text-sm" data-testid={`removed-${kind || "all"}`}>
      <summary className="cursor-pointer font-medium">{`Removed records (${data.items.length})`}</summary>
      <div className="divide-y mt-2">{data.items.map((a) => <RemovedRow key={a.id} a={a} act={act} owner={user.role === "owner"} />)}</div>
    </details>
  );
}
